import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Building2, Users, UserCheck, Handshake, Shield, Award, Clock, CheckCircle } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
export const HeroSection = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-primary/5 via-background to-secondary/5 min-h-[90vh] flex">
      {/* Enhanced Background Effects with Parallax */}
      <div className="absolute inset-0 w-full h-full">
        <motion.div 
          className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl"
          animate={{ 
            x: [0, 30, 0],
            y: [0, -20, 0],
            scale: [1, 1.1, 1]
          }}
          transition={{ 
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div 
          className="absolute bottom-10 right-10 w-96 h-96 bg-primary/5 rounded-full blur-3xl"
          animate={{ 
            x: [0, -40, 0],
            y: [0, 30, 0],
            scale: [1, 0.9, 1]
          }}
          transition={{ 
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div 
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-primary/3 to-secondary/3 rounded-full blur-3xl"
          animate={{ 
            rotate: [0, 360],
            scale: [1, 1.2, 1]
          }}
          transition={{ 
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
        />
      </div>

      <div className="container mx-auto px-4 py-6 lg:py-2 relative z-10">
        <div className="grid lg:grid-cols-3 gap-8 items-start">
          {/* Left Column - Content (Primary Focus) */}
          <div className="lg:col-span-2 text-left lg:pr-8 stagger-animation bg-gradient-to-br from-card/30 to-transparent rounded-2xl p-8 backdrop-blur-sm border border-border/10">
            {isMobile && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="flex items-center mb-6"
              >
                <span className="bg-secondary/10 text-secondary text-sm font-semibold px-4 py-2 rounded-full border border-secondary/20">
                  Not a brokerage — AI-powered platform
                </span>
              </motion.div>
            )}

            <motion.div className="mb-8" initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }}>
              <h1 className="font-bold text-gray-900 mb-6 leading-tight">
                <span className="block text-5xl md:text-6xl lg:text-7xl mb-4">
                  Find the Right PEO Quickly — and
                </span>
                <span className="block text-5xl md:text-6xl lg:text-7xl">
                  <span className="bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent">Save Thousands Per Employee</span>
                </span>
              </h1>
            </motion.div>
            
            <motion.p className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed max-w-2xl" initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }}>Running a small business is hard enough — chasing HR and insurance quotes shouldn't be. With one simple application, The PEO App delivers multiple quotes from top providers in days, not weeks. That means lower costs for your business, better benefits for your team, and more time for you to focus on growth.</motion.p>

            {/* Primary CTA - Moved Up for Better Visibility */}
            <motion.div className="mb-8" initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.3
            }}>
              <div className="bg-primary/5 border border-primary/20 rounded-xl p-6 mb-6">
                <div className="flex items-center gap-3 mb-3">
                  <CheckCircle className="w-5 h-5 text-primary" />
                  <span className="text-sm font-semibold text-primary">100% Free • No Commitment • Instant Access</span>
                </div>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button size="lg" className="bg-primary hover:bg-primary/90 text-white px-10 py-6 rounded-full relative overflow-hidden group shadow-lg hover:shadow-xl text-lg flex-1 sm:flex-initial" onClick={() => navigate("/signup")}>
                    <span className="relative z-10 flex items-center justify-center">
                      Get Your Free Account Today
                      <ArrowRight className="ml-3 w-6 h-6 group-hover:translate-x-1 transition-transform" />
                    </span>
                    <span className="absolute inset-0 w-full h-full bg-gradient-shimmer -translate-x-full group-hover:translate-x-full transition-transform duration-1000 opacity-20"></span>
                  </Button>
                  
                  <Button variant="outline" size="lg" className="px-8 py-6 rounded-full hover:bg-gray-100/80 text-lg" onClick={() => navigate("/how-it-works")}>
                    See How It Works
                  </Button>
                </div>
              </div>
            </motion.div>

            {/* Condensed Feature Highlights */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6"
            >
              <motion.div 
                className="group flex items-center gap-4 text-foreground p-4 bg-gradient-to-br from-card to-card/50 rounded-xl border border-border/20 backdrop-blur-sm hover:shadow-lg transition-all duration-300 cursor-pointer"
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <Shield className="w-6 h-6 text-primary group-hover:scale-110 transition-transform" />
                </div>
                <div className="flex-1">
                  <span className="font-semibold text-foreground block mb-1">Data Security</span>
                  <span className="text-xs text-muted-foreground">HIPAA & SOC 2 compliant</span>
                </div>
              </motion.div>
              
              <motion.div 
                className="group flex items-center gap-4 text-foreground p-4 bg-gradient-to-br from-card to-card/50 rounded-xl border border-border/20 backdrop-blur-sm hover:shadow-lg transition-all duration-300 cursor-pointer"
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <Award className="w-6 h-6 text-primary group-hover:scale-110 transition-transform" />
                </div>
                <div className="flex-1">
                  <span className="font-semibold text-foreground block mb-1">Patent Pending</span>
                  <span className="text-xs text-muted-foreground">Cutting-edge AI technology</span>
                </div>
              </motion.div>
              
              <motion.div 
                className="group flex items-center gap-4 text-foreground p-4 bg-gradient-to-br from-card to-card/50 rounded-xl border border-border/20 backdrop-blur-sm hover:shadow-lg transition-all duration-300 cursor-pointer"
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <Clock className="w-6 h-6 text-primary group-hover:scale-110 transition-transform" />
                </div>
                <div className="flex-1">
                  <span className="font-semibold text-foreground block mb-1">Time-Saving</span>
                  <span className="text-xs text-muted-foreground">Streamlined processes</span>
                </div>
              </motion.div>
            </motion.div>
          </div>

          {/* Right Column - Secondary Paths */}
          <div className="lg:col-span-1 hidden lg:block">
            <motion.div 
              className="space-y-4 opacity-80"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 0.8, x: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Other Services</h3>
              
              <div className="space-y-3">
                {/* Primary CTA for Small Businesses - Highlighted */}
                <motion.div 
                  className="group p-4 bg-primary/5 border-2 border-primary/20 rounded-lg hover:bg-primary/10 transition-all duration-300 cursor-pointer"
                  whileHover={{ scale: 1.02 }}
                  onClick={() => navigate("/signup")}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-primary text-sm">Small Businesses</h4>
                      <p className="text-xs text-primary/70">Start here - Free quotes</p>
                    </div>
                  </div>
                </motion.div>
                
                {/* Secondary options */}
                <motion.div 
                  className="group p-3 bg-card/30 rounded-lg hover:bg-card/50 transition-all duration-300 cursor-pointer border border-border/10"
                  whileHover={{ scale: 1.01 }}
                  onClick={() => navigate("/for-brokers")}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-muted/50 rounded-lg flex items-center justify-center">
                      <UserCheck className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <div>
                      <h4 className="font-medium text-muted-foreground text-sm">Brokers</h4>
                    </div>
                  </div>
                </motion.div>
                
                <motion.div 
                  className="group p-3 bg-card/30 rounded-lg hover:bg-card/50 transition-all duration-300 cursor-pointer border border-border/10"
                  whileHover={{ scale: 1.01 }}
                  onClick={() => navigate("/for-peos")}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-muted/50 rounded-lg flex items-center justify-center">
                      <Users className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <div>
                      <h4 className="font-medium text-muted-foreground text-sm">PEO Providers</h4>
                    </div>
                  </div>
                </motion.div>
                
                <motion.div 
                  className="group p-3 bg-card/30 rounded-lg hover:bg-card/50 transition-all duration-300 cursor-pointer border border-border/10"
                  whileHover={{ scale: 1.01 }}
                  onClick={() => navigate("/partnerships")}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-muted/50 rounded-lg flex items-center justify-center">
                      <Handshake className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <div>
                      <h4 className="font-medium text-muted-foreground text-sm">Partners</h4>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};